import { of } from 'rxjs'
import { isEmpty } from 'ramda'
import { ofType } from 'redux-observable'
import {
  mapTo,
  mergeMap,
  switchMap,
  catchError,
  take,
  filter,
  withLatestFrom
} from 'rxjs/operators'
import Actions from '../actions'
import { getPayload } from '../utils/rxUtils'
import { getJSON, patchJSON } from '../utils/fetchUtils'
import { VISIT_SUMMARY_FLAG } from '../consts'
import { flatten } from 'lodash'
import { getValidFamilyMemberIds } from '../utils/familyUtils'
import { fetchMultiplePatientData } from '../utils/medicalInfoUtils'

const catchAndDispatchCustomError = catchError(error => {
  console.error(error)

  return of(Actions.createAction(Actions.OPERATION_FAILED, { error }))
})

export const requestMedicalInfoWhenLoggedIn = action$ =>
  action$.pipe(
    getPayload(Actions.IS_SIGNED_IN_RECEIVED),
    filter(({ isSignedIn }) => isSignedIn),
    mergeMap(() => {
      if (VISIT_SUMMARY_FLAG) {
        return of(
          Actions.createAction(Actions.PATIENT_DOCS_REQUESTED),
          Actions.createAction(Actions.VISIT_SUMMARY_REQUESTED)
        )
      } else {
        return of(Actions.createAction(Actions.PATIENT_DOCS_REQUESTED))
      }
    })
  )

export const requestMedicalInfo = action$ =>
  action$.pipe(
    getPayload(Actions.USER_LOGGED_IN_SUCCSESSFULY),
    take(1),
    mergeMap(() => {
      if (VISIT_SUMMARY_FLAG) {
        return of(
          Actions.createAction(Actions.PATIENT_DOCS_REQUESTED),
          Actions.createAction(Actions.VISIT_SUMMARY_REQUESTED)
        )
      } else {
        return of(Actions.createAction(Actions.PATIENT_DOCS_REQUESTED))
      }
    })
  )

export const requestMedicalInfoAfterAppointmentCompletes = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.CALL_ENDED_BY_PATIENT, Actions.ROOM_COMPLETED),
    mergeMap(() => {
      if (VISIT_SUMMARY_FLAG) {
        return of(
          Actions.createAction(Actions.PATIENT_DOCS_REQUESTED),
          Actions.createAction(Actions.VISIT_SUMMARY_REQUESTED),
          Actions.createAction(Actions.APPOINTMENTS_POLL_REQUESTED)
        )
      } else {
        return of(
          Actions.createAction(Actions.PATIENT_DOCS_REQUESTED),
          Actions.createAction(Actions.APPOINTMENTS_POLL_REQUESTED)
        )
      }
    })
  )

export const getPatientDocs = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PATIENT_DOCS_REQUESTED),
    switchMap(() => {
      return getJSON(`/documents/patient_docs/`).pipe(
        switchMap(
          ({
            pastVisitsLists,
            workNotesLists,
            referralsLists,
            testResultsLists,
            prescriptionsLists,
            consentsLists
          }) => {
            return of(
              Actions.createAction(Actions.PATIENT_DOCS_RECIEVED, {
                pastVisitsLists: flatten(Object.values(pastVisitsLists)),
                workNotesLists: flatten(Object.values(workNotesLists)),
                referralsLists: flatten(Object.values(referralsLists)),
                testResultsLists: flatten(Object.values(testResultsLists)),
                prescriptionsLists: flatten(Object.values(prescriptionsLists)),
                consentsLists: flatten(Object.values(consentsLists))
              })
            )
          }
        ),
        catchError(e => {
          return of(Actions.createAction(Actions.PATIENT_DOCS_FAILED))
        })
      )
    })
  )

export const getQuestReferralsResults = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.QUEST_REFERRALS_RESULTS_REQUESTED),
    withLatestFrom(state$),
    switchMap(([_, state]) => {
      const validIds = getValidFamilyMemberIds(state, true)

      if (validIds.length === 0) {
        return of(
          Actions.createAction(Actions.QUEST_REFERRALS_RESULTS_RECEIVED, [])
        )
      }

      return fetchMultiplePatientData({
        validIds,
        apiEndpoint: '/hglabs/patresult/',
        dataType: 'results',
        resultStructure: []
      }).pipe(
        switchMap(responses => {
          // Combine all responses into a single array
          const combinedResponse = responses.reduce((acc, curr) => {
            if (Array.isArray(curr)) {
              const validResults = curr.filter(res => !isEmpty(res?.result))
              return acc.concat(validResults)
            }
            return acc
          }, [])

          return of(
            Actions.createAction(
              Actions.QUEST_REFERRALS_RESULTS_RECEIVED,
              combinedResponse
            )
          )
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const getQuestReferralsResultsFromNotification = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.QUEST_REFERRALS_RESULTS_REQUESTED_FROM_NOTIFICATION),
    switchMap(({ patientId, referralResultId }) => {
      return getJSON(`/hglabs/patresult/${patientId}/`).pipe(
        switchMap(response => {
          return of(
            Actions.createAction(
              Actions.QUEST_REFERRALS_RESULTS_RECEIVED,
              response
            ),
            Actions.createAction(
              Actions.NAV_TO_REFERRAL_RESULT_FROM_NOTIFICATIONS,
              referralResultId
            )
          )
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const updateSeenStatusForReferrals = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.UPDATE_QUEST_REFERRALS_RESULTS_SEEN_STATUS_REQUESTED),
    switchMap(({ id }) => {
      return patchJSON(`/hglabs/patresult/${id}/`).pipe(
        switchMap(response => {
          return of(
            Actions.createAction(
              Actions.UPDATE_QUEST_REFERRALS_RESULTS_SEEN_STATUS_RECEIVED,
              response
            )
          )
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const getReferralPdf = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.REFERRAL_PDF_REQUESTED),
    switchMap(({ pdfLink }) => {
      return getJSON(pdfLink, '').pipe(
        switchMap(response => {
          return of(Actions.REFERRAL_PDF_RECIEVED)
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const getQuestReferrals = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.QUEST_REFERRALS_REQUESTED),
    filter(({ id }) => !isEmpty(id)),
    switchMap(({ id }) => {
      return getJSON(`/hglabs/quest/referral/${id}/`).pipe(
        switchMap(({ msg }) => {
          return of(
            Actions.createAction(Actions.QUEST_REFERRALS_RECEIVED, {
              result: msg
            }),
            Actions.createAction(Actions.GENERIC_REFERRALS_RECEIVED)
          )
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const getFamilyQuestReferrals = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.QUEST_REFERRALS_REQUESTED),
    filter(({ id }) => isEmpty(id)),
    withLatestFrom(state$),
    switchMap(([_, state]) => {
      const validIds = getValidFamilyMemberIds(state, true)

      if (validIds.length === 0) {
        return of(
          Actions.createAction(Actions.QUEST_REFERRALS_RECEIVED, {
            result: []
          }),
          Actions.createAction(Actions.GENERIC_REFERRALS_RECEIVED)
        )
      }

      return fetchMultiplePatientData({
        validIds,
        apiEndpoint: '/hglabs/quest/referral/',
        dataType: 'referrals',
        resultStructure: { msg: [] }
      }).pipe(
        switchMap(responses => {
          // Combine all responses into a single array
          const combinedResponse = responses.reduce((acc, curr) => {
            return acc.concat(curr?.msg || [])
          }, [])

          return of(
            Actions.createAction(Actions.QUEST_REFERRALS_RECEIVED, {
              result: combinedResponse
            }),
            Actions.createAction(Actions.GENERIC_REFERRALS_RECEIVED)
          )
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const getGenericReferral = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.GENERIC_REFERRALS_REQUESTED),
    switchMap(({ orderId, patientId, fileId, url }) => {
      return patchJSON(`/hglabs/quest/referral/${orderId}/`).pipe(
        switchMap(response => {
          return of(
            Actions.createAction(Actions.QUEST_REFERRALS_REQUESTED, {
              id: patientId
            }),
            Actions.createAction(Actions.NAVIGATED_TO, `${url}/${fileId}`)
          )
        }),
        catchError(e => {
          return of(
            Actions.createAction(Actions.GENERIC_REFERRALS_RECEIVE_FAILED)
          )
        })
      )
    })
  )

export const getLabFacilityLocation = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.SELECTED_LAB_FACILITY_LOCATION_REQUESTED),
    switchMap(({ locationId, orderId }) => {
      return getJSON(
        `/hglabs/lab/location/details/?location_id=${locationId}&order_id=${orderId}`
      ).pipe(
        switchMap(response => {
          return of(
            Actions.createAction(
              Actions.SELECTED_LAB_FACILITY_LOCATION_RECEIVED,
              response
            )
          )
        }),
        catchError(e => {
          return of(
            Actions.createAction(Actions.SELECTED_LAB_FACILITY_LOCATION_FAILED)
          )
        })
      )
    })
  )

export const getVisitSummary = (action$, state$) =>
  action$.pipe(
    ofType(Actions.VISIT_SUMMARY_REQUESTED),
    switchMap(() => {
      return getJSON(`/documents/visit-summery/patient/`).pipe(
        switchMap(item => {
          return of(
            Actions.createAction(Actions.VISIT_SUMMARY_RECEIVED, {
              visitSummary: item
            })
          )
        }),
        catchError(e => {
          return of(Actions.createAction(Actions.VISIT_SUMMARY_FAILED))
        })
      )
    })
  )

export const getPatientFile = action$ =>
  action$.pipe(
    getPayload(Actions.PATIENT_FILE_REQUESTED),
    switchMap(({ patientFileID }) => {
      return getJSON(`/documents/files/${patientFileID}/`).pipe(
        switchMap(({ file }) => {
          return of(
            Actions.createAction(Actions.PATIENT_FILE_RECEIVED, {
              file,
              patientFileID
            })
          )
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const getPastVisit = action$ =>
  action$.pipe(
    getPayload(Actions.PAST_VISIT_REQUESTED),
    switchMap(({ recordId, url }) => {
      return getJSON(`/past-visits/${recordId}/`).pipe(
        switchMap(({ item }) => {
          return of(
            Actions.createAction(Actions.PAST_VISIT_RECEIVED, {
              item,
              recordId
            }),
            Actions.createAction(Actions.NAVIGATED_TO, `${url}/${recordId}`)
          )
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const pastVisitSelected = actions$ =>
  actions$.pipe(
    ofType(Actions.SHOW_PAST_VISIT),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, 'PastVisitScreen'))
  )

export const workNoteSelected = actions$ =>
  actions$.pipe(
    ofType(Actions.SHOW_WORK_NOTE),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, 'WorkNoteScreen'))
  )

export const referralSelected = actions$ =>
  actions$.pipe(
    ofType(Actions.SHOW_REFERRAL),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, 'ReferralScreen'))
  )

export const prescriptionSelected = actions$ =>
  actions$.pipe(
    ofType(Actions.SHOW_PRESCRIPTIONS),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, 'PrescriptionScreen'))
  )

export const telenedicineConsentSelected = actions$ =>
  actions$.pipe(
    ofType(Actions.SHOW_CONSENTS),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, 'ConsentScreen'))
  )
